import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, InputNumber, Row, Select, Space, Statistic, Table, Tag } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import PageHeader from '../../components/business/PageHeader';
import RequestState from '../../components/business/RequestState';
import { MailDispatchEvent, MailDispatchOperationsStatus } from '../../models/mail';
import { PageResult } from '../../models/v2';
import { V2ApiError, v2Api } from '../../services/v2';
import { asV2Error, formatDateTime } from '../../utils/v2';
import { MAIL_PAGE_SIZE_OPTIONS } from './mailPagination';

const emptyEvents: PageResult<MailDispatchEvent> = {
    items: [], pageNo: 1, pageSize: 20, total: 0,
};

const eventTypeLabels: { [key: string]: string } = {
    SERVICE_STARTED: '服务启动',
    SERVICE_STOPPED: '服务停止',
    UNCLEAN_STOP_DETECTED: '异常中断',
    SCHEDULER_POLL_FAILED: '调度异常',
    DISPATCH_RECOVERY: '任务恢复',
    PROVIDER_UNAVAILABLE: '服务商不可用',
    PRE_SUBMIT_FAILURE: '提交前失败',
    TEMPLATE_DATA_MISSING: '模板数据不足',
    DISPATCH_RETRYABLE_FAILURE: '发送失败待重试',
    DISPATCH_FINAL_FAILURE: '发送最终失败',
    DISPATCH_UNKNOWN: '发送结果未知',
    PRE_SUBMIT_DEFERRED: '发送条件不满足',
    RATE_LIMIT_WAIT: '等待发送速率',
    DAILY_QUOTA_WAIT: '等待每日配额',
    BATCH_AUTO_PAUSED: '历史记录：批次自动暂停',
    BATCH_PAUSE: '人工暂停批次',
    BATCH_RESUME: '恢复批次',
    BATCH_CANCEL: '取消批次',
    RECONCILIATION_QUERY_FAILED: '自动对账查询失败',
    RECONCILIATION_SAVE_FAILED: '对账保存失败',
    DISPATCH_NO_PROGRESS: '调度没有推进',
    ACCOUNT_STOP_ACTIVE: '账号级停发',
    CONTROL_VERSION_MISMATCH: '任务控制版本异常',
};

const severityView = (severity: string) => {
    if (severity === 'ERROR') return <Tag color="red">错误</Tag>;
    if (severity === 'WARN') return <Tag color="orange">警告</Tag>;
    return <Tag color="blue">信息</Tag>;
};

const MailDispatchOperationsPage = () => {
    const [status, setStatus] = useState<MailDispatchOperationsStatus | null>(null);
    const [events, setEvents] = useState(emptyEvents);
    const [filters, setFilters] = useState<any>({ pageNo: 1, pageSize: 20 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<V2ApiError | null>(null);

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const [nextStatus, nextEvents] = await Promise.all([
                v2Api.mailDispatchOperationsStatus(),
                v2Api.mailDispatchEvents(filters),
            ]);
            setStatus(nextStatus);
            setEvents(nextEvents);
            setError(null);
        } catch (nextError) {
            setError(asV2Error(nextError));
        } finally {
            if (!silent) setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        const timer = window.setInterval(() => {
            if (!document.hidden) load(true);
        }, 10000);
        return () => window.clearInterval(timer);
    }, [load]);

    const latestWorker = status?.workers && status.workers[0];
    const statusType = status?.statusCode === 'RUNNING'
        ? 'success'
        : status?.statusCode === 'DEGRADED' || status?.statusCode === 'OFFLINE'
            ? 'error'
            : 'warning';

    const columns = [
        {
            title: '发生时间', dataIndex: 'occurredAt', width: 175,
            render: (value: string) => formatDateTime(value),
        },
        {
            title: '级别', dataIndex: 'severity', width: 80,
            render: (value: string) => severityView(value),
        },
        {
            title: '事件', dataIndex: 'eventType', width: 150,
            render: (value: string) => eventTypeLabels[value] || value,
        },
        {
            title: '批次/任务', key: 'scope', width: 145,
            render: (_: any, row: MailDispatchEvent) => (
                <span>
                    {row.batchId ? `批次 #${row.batchId}` : '系统'}
                    {row.sendRecipientId ? <span className="v2-subline">任务 #{row.sendRecipientId}</span> : null}
                </span>
            ),
        },
        {
            title: '运行说明', key: 'message',
            render: (_: any, row: MailDispatchEvent) => (
                <span>
                    <strong>{row.title}</strong>
                    {row.detail ? <span className="v2-subline">{row.detail}</span> : null}
                </span>
            ),
        },
    ];

    return (
        <div className="v2-page mail-page">
            <PageHeader
                eyebrow="批量邮件 · 运维可观测性"
                title="发送运行监控"
                extra={<Button icon={<ReloadOutlined />} loading={loading} onClick={() => load()}>立即刷新</Button>}
            />
            <RequestState loading={loading && !status} error={error} onRetry={() => load()}>
                {status && (
                    <>
                        <Alert
                            showIcon
                            type={statusType}
                            message={status.statusTitle}
                            description={status.statusExplanation}
                            style={{ marginBottom: 16 }}
                        />
                        <Row gutter={[12, 12]} className="v2-stat-grid mail-operations-stat-grid">
                            <Col xs={12} sm={8} md={6} xl={4}><Card className="mail-operations-stat-card"><Statistic title="活动批次" value={status.activeBatchCount || 0} /></Card></Col>
                            <Col xs={12} sm={8} md={6} xl={4}><Card className="mail-operations-stat-card"><Statistic title="等待立即发送" value={status.dueRecipientCount || 0} /></Card></Col>
                            <Col xs={12} sm={8} md={6} xl={4}><Card className="mail-operations-stat-card"><Statistic title="限速/配额等待" value={status.deferredRecipientCount || 0} /></Card></Col>
                            <Col xs={12} sm={8} md={6} xl={4}><Card className="mail-operations-stat-card"><Statistic title="提交处理中" value={status.inFlightCount || 0} /></Card></Col>
                            <Col xs={12} sm={8} md={6} xl={4}><Card className="mail-operations-stat-card"><Statistic title="未知待对账" value={status.unknownCount || 0} /></Card></Col>
                            <Col xs={12} sm={8} md={6} xl={4}><Card className="mail-operations-stat-card"><Statistic title="账号停发影响批次" value={status.accountStoppedBatchCount || 0} /></Card></Col>
                            <Col xs={12} sm={8} md={6} xl={4}><Card className="mail-operations-stat-card"><Statistic title="配置异常等待批次" value={status.configurationBlockedBatchCount || 0} /></Card></Col>
                            <Col xs={12} sm={8} md={6} xl={4}><Card className="mail-operations-stat-card"><Statistic title="控制版本异常任务" value={status.controlVersionMismatchCount || 0} /></Card></Col>
                            <Col xs={12} sm={8} md={6} xl={4}><Card className="mail-operations-stat-card"><Statistic title="长时间未推进批次" value={status.stalledBatchCount || 0} /></Card></Col>
                            <Col xs={12} sm={8} md={6} xl={4}><Card className="mail-operations-stat-card is-date-time"><Statistic title="最后成功提交" value={formatDateTime(status.lastSubmittedAt)} /></Card></Col>
                            <Col xs={12} sm={8} md={6} xl={4}><Card className="mail-operations-stat-card is-date-time"><Statistic title="下一调度时间" value={formatDateTime(status.nextDispatchAt)} /></Card></Col>
                            <Col xs={12} sm={8} md={6} xl={4}><Card className="mail-operations-stat-card is-date-time"><Statistic title="最后服务心跳" value={formatDateTime(latestWorker?.lastHeartbeatAt)} /></Card></Col>
                        </Row>
                        {latestWorker && (
                            <Card size="small" style={{ marginBottom: 16 }}>
                                <Space className="v2-wrap-space">
                                    <span>实例：{latestWorker.workerId || '—'}</span>
                                    <span>主机：{latestWorker.hostName || '—'}</span>
                                    <span>进程：{latestWorker.processId || '—'}</span>
                                    <span>启动：{formatDateTime(latestWorker.startedAt)}</span>
                                    {latestWorker.stopReason && <span>停止说明：{latestWorker.stopReason}</span>}
                                </Space>
                            </Card>
                        )}
                    </>
                )}
                <Card title="运行事件时间线" bordered={false}>
                    <Space className="mail-table-filter v2-wrap-space" style={{ marginBottom: 12 }}>
                        <Select
                            allowClear placeholder="日志级别" style={{ width: 130 }}
                            value={filters.severity}
                            onChange={(severity) => setFilters({ ...filters, severity, pageNo: 1 })}
                        >
                            <Select.Option value="INFO">信息</Select.Option>
                            <Select.Option value="WARN">警告</Select.Option>
                            <Select.Option value="ERROR">错误</Select.Option>
                        </Select>
                        <Select
                            allowClear showSearch placeholder="事件类型" style={{ width: 210 }}
                            value={filters.eventType}
                            onChange={(eventType) => setFilters({ ...filters, eventType, pageNo: 1 })}
                        >
                            {Object.keys(eventTypeLabels).map((key) => (
                                <Select.Option key={key} value={key}>{eventTypeLabels[key]}</Select.Option>
                            ))}
                        </Select>
                        <InputNumber
                            min={1} precision={0} placeholder="批次编号" style={{ width: 150 }}
                            value={filters.batchId}
                            onChange={(batchId) => setFilters({ ...filters, batchId, pageNo: 1 })}
                        />
                    </Space>
                    <Table
                        rowKey="id" columns={columns} dataSource={events.items}
                        scroll={{ x: 1000 }}
                        pagination={{
                            current: events.pageNo, pageSize: events.pageSize, total: events.total,
                            showSizeChanger: true, pageSizeOptions: MAIL_PAGE_SIZE_OPTIONS,
                            showTotal: (total) => `共 ${total} 条运行记录`,
                            onChange: (pageNo, pageSize) => setFilters({ ...filters, pageNo, pageSize }),
                        }}
                    />
                </Card>
            </RequestState>
        </div>
    );
};

export default MailDispatchOperationsPage;
